import React, { useEffect } from "react";
import { z } from "zod";
import type { RouteComponentProps } from "@gatsbyjs/reach-router";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { navigate } from "utilities/navigate";
import {
  Typography,
  Form,
  Input,
  NativeSelect,
  DateInput,
  Checkbox,
  Button,
  PageLoader,
  Alert,
  toast,
} from "@picklebet/ui";
import { Link } from "@/features/shell/components/shared/Link/Link";
import { getAllCountries } from "utilities/address";
import { userVerificationSchema } from "./schemas/userVerificationSchema";
import { LEGAL_NAME } from "utilities/regex";
import { useProfile } from "@/hooks/firestore/user/useProfile";
import { verificationService } from "features/account/services/verification";
import { useVerificationFormAttempt } from "features/account/hooks/useVerifications";
import { useCustomerLicence } from "@/features/auth/hooks/usePermission";
import { handleFailedVerificationAttempt } from "features/account/utils/verification";
import { telemetry } from "@/utilities/telemetry";
import { DVSConsent } from "@/features/account/components/verification/DVSConsent";
import { VERIFICATION_DATA_SOURCES } from "features/account/types/Verification";
import { isValidFutureDate } from "utilities/datetime";

const codesWithoutAustralia = getAllCountries(true).filter(
  (country) => country.value !== "AU",
);

const schema = userVerificationSchema.extend({
  middleName: z.string().regex(LEGAL_NAME, "Invalid middle name").optional(),
  number: z.string().min(1, "Passport document number is required"),
  countryOfIssue: z.string().min(1, "Country of issue is required"),
  expiryDate: z
    .string({ error: "A valid expiry date is required" })
    .refine(isValidFutureDate, {
      message: "Your expiry date is invalid or visa has expired",
    }),
});

type FormValues = z.infer<typeof schema>;

type AustralianVisaProps = {
  prefix?: string;
} & RouteComponentProps;

const AustralianVisa = ({ prefix = "/account" }: AustralianVisaProps) => {
  const licence = useCustomerLicence();
  const { attempt, isLoading, hasSubmitted, markSubmitted } =
    useVerificationFormAttempt();

  const {
    control,
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
    setValue,
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
  });

  const { profile } = useProfile();

  useEffect(() => {
    if (profile) {
      setValue("firstName", profile.firstName);
      setValue("lastName", profile.lastName);
      setValue("dateOfBirth", profile.dateOfBirth);
    }
  }, [profile]);

  const consent = watch("consent", false);

  const onSubmit = async ({ consent, ...fields }: FormValues) => {
    if (!attempt) {
      toast({
        variant: "danger",
        description:
          "Something went wrong. Please refresh and try again or contact support.",
      });
      return;
    }

    const verificationType = VERIFICATION_DATA_SOURCES.AUSTRALIAN_VISA;
    const normalizedFields = {
      ...fields,
      number: fields.number.replace(/\s/g, ""),
    };

    const result = await verificationService.submitAttemptData({
      attemptId: attempt.attemptId,
      attemptType: "VIXIdentityDocuments",
      type: "AustralianVisaCheck",
      terms: consent,
      fields: normalizedFields,
    });

    if (!result.ok) {
      handleFailedVerificationAttempt(verificationType, licence);
      return;
    }

    telemetry.submittedVerificationForm({
      outcome: "success",
      type: verificationType,
      licence: licence ?? undefined,
    });

    markSubmitted();
    navigate(`${prefix}/verification`);
  };

  if (isLoading || hasSubmitted) return <PageLoader />;

  if (!attempt && !isSubmitting) {
    return (
      <Alert
        color="danger"
        actions={
          <Button
            onClick={() => window.location.reload()}
            size="md"
            variant="danger"
          >
            Refresh
          </Button>
        }
      >
        Unable to load verification option, please refresh to try again.
      </Alert>
    );
  }

  return (
    <>
      <Form
        onSubmit={handleSubmit(onSubmit)}
        fixedWidth
        disabled={isSubmitting}
      >
        <Typography variant="h4" noSpacing>
          Australian Visa
        </Typography>

        <Typography variant="body">
          Please enter your identity information, exactly as it appears on your
          Australian Visa (foreign passport).
        </Typography>

        <Typography variant={`body`}>
          Don't have an Australian Visa? Select from a list of{" "}
          <Link to={`${prefix}/verification`}>Verification options</Link>.
        </Typography>

        <Input
          {...register("number")}
          label={`Passport Number`}
          errorMessage={errors?.number?.message}
          maxLength={14}
        />

        <NativeSelect
          {...register("countryOfIssue")}
          label={`Passport country of issue`}
          errorMessage={errors?.countryOfIssue?.message}
          options={codesWithoutAustralia}
        />

        <Controller
          control={control}
          name={"expiryDate"}
          render={({ field: { ref: _, ...field } }) => (
            <DateInput
              id={"expiryDate"}
              {...field}
              errorMessage={errors.expiryDate?.message}
              label={`Expiry Date`}
            />
          )}
        />

        <Input
          {...register("firstName")}
          label={`First Name`}
          errorMessage={errors?.firstName?.message}
        />

        <Input
          {...register("middleName")}
          label={`Middle Name`}
          errorMessage={errors?.middleName?.message}
        />

        <Input
          {...register("lastName")}
          label={`Last Name`}
          errorMessage={errors?.lastName?.message}
        />

        <Controller
          control={control}
          name={"dateOfBirth"}
          render={({ field: { ref: _, ...field } }) => (
            <DateInput
              id={"dateOfBirth"}
              {...field}
              errorMessage={errors.dateOfBirth?.message}
              label={`Date of Birth`}
            />
          )}
        />

        <Controller
          control={control}
          render={({ field: { onChange, value: _, ...field } }) => (
            <Checkbox
              id={`consent`}
              label={DVSConsent}
              checked={consent}
              onCheckedChange={(checked) => onChange?.(checked === true)}
              hasError={!!errors?.consent?.message}
              {...field}
            />
          )}
          name={`consent`}
        />

        <Button type={`submit`} loading={isSubmitting}>
          Verify Visa
        </Button>
      </Form>
    </>
  );
};

export { AustralianVisa };
